/**
 * API configuration
 * 
 * Web (production on app.cleariest.com): use api.cleariest.com directly
 * Other web hosts (dev/preview): use relative paths for proxy-based routing
 * Web (development): Vite proxies /api/* to localhost:5000 (configured in vite.config.ts)
 * Mobile (native): Uses absolute URL to the production backend (no proxy available)
 * 
 * The app detects the platform and uses the appropriate base URL.
 */

import { Capacitor, CapacitorHttp } from '@capacitor/core';

const TRANSIENT_RETRY_STATUSES = new Set([502, 503, 504]);
const TRANSIENT_RETRY_DELAYS_MS = [300, 800, 1500] as const;
const ACCESS_TOKEN_PROVIDER_TIMEOUT_MS = 5000;
const NATIVE_FETCH_HARD_TIMEOUT_MS = 35000;
const NATIVE_HTTP_CONNECT_TIMEOUT_MS = 15000;
const NATIVE_HTTP_READ_TIMEOUT_MS = 35000;
const WEB_API_FALLBACK_BASE = 'https://api.cleariest.com';
const WEB_APP_HOSTNAME = 'app.cleariest.com';

const isNativeRuntime = () => Capacitor.isNativePlatform();
const isAndroidRuntime = () => Capacitor.getPlatform() === 'android';

/**
 * Determine if a request with the given method and status code should be retried.
 * GET/HEAD: always retry on transient errors.
 * POST/PUT/PATCH/DELETE: retry on 502/503/504 (server didn't process the request).
 * Never retry on 400/401/403/404/409/422 — these indicate client errors.
 */
const shouldRetryTransient = (_method: string, status: number) => {
  return TRANSIENT_RETRY_STATUSES.has(status);
};

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

type NativeHttpResult = {
  status: number;
  data: unknown;
  headers?: Record<string, string>;
};

const NULL_BODY_STATUSES = new Set([204, 205, 304]);

function toResponseFromNative(result: NativeHttpResult): Response {
  const headers = new Headers(result.headers ?? {});
  const contentType = headers.get('content-type') ?? 'application/json';

  if (NULL_BODY_STATUSES.has(result.status)) {
    return new Response(null, { status: result.status, headers });
  }

  if (typeof result.data === 'string') {
    return new Response(result.data, { status: result.status, headers });
  }

  if (result.data == null) {
    return new Response('', { status: result.status, headers });
  }

  if (!headers.has('content-type')) {
    headers.set('content-type', contentType);
  }

  return new Response(JSON.stringify(result.data), { status: result.status, headers });
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);

    promise
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

/**
 * Get the mobile API URL based on environment and platform.
 * 
 * Priority:
 * 1. VITE_MOBILE_API_URL env var (explicit override)
 * 2. VITE_MOBILE_DEV_MODE=true -> use local dev endpoints
 *    - Android + VITE_MOBILE_ANDROID_USE_ADB_REVERSE=true => localhost (USB + adb reverse)
 *    - Android default dev => 10.0.2.2 (emulator)
 *    - iOS dev => localhost
 * 3. Production: api.cleariest.com
 */
const getMobileApiUrl = (): string => {
  // Allow explicit override via env var
  if (import.meta.env.VITE_MOBILE_API_URL) {
    return import.meta.env.VITE_MOBILE_API_URL;
  }
  
  // Check for mobile dev mode flag (set in Doppler dev config)
  // This allows dev mobile builds to hit local backend
  const isMobileDevMode = import.meta.env.VITE_MOBILE_DEV_MODE === 'true';
  const useAndroidAdbReverse = import.meta.env.VITE_MOBILE_ANDROID_USE_ADB_REVERSE === 'true';
  
  if (isMobileDevMode) {
    if (isAndroidRuntime()) {
      // Physical Android device over USB + `adb reverse tcp:5000 tcp:5000`
      // can reach host machine via localhost.
      if (useAndroidAdbReverse) {
        return 'http://localhost:5000';
      }

      // Android emulator uses 10.0.2.2 to reach the host machine's localhost
      return 'http://10.0.2.2:5000';
    }

    // iOS simulator shares the host's network, so localhost works
    return 'http://localhost:5000';
  }
  
  // Production: use the production API
  return 'https://api.cleariest.com';
};

const MOBILE_API_URL = getMobileApiUrl();

type AccessTokenProvider = () => Promise<string | null>;

let accessTokenProvider: AccessTokenProvider | null = null;

export const setAccessTokenProvider = (provider: AccessTokenProvider | null) => {
  accessTokenProvider = provider;
};

export const getApiBaseUrl = () => {
  // Native mobile apps need absolute URL (no proxy available)
  if (isNativeRuntime()) {
    return MOBILE_API_URL;
  }
  // Production web: route directly to API domain to avoid proxy routing loops.
  if (typeof window !== 'undefined' && window.location.hostname === WEB_APP_HOSTNAME) {
    return WEB_API_FALLBACK_BASE;
  }
  // Web uses relative path - nginx (prod) or Vite (dev) handles proxying
  return '';
};

export const buildApiUrl = (path: string) => {
  const baseUrl = getApiBaseUrl();
  
  // If already an absolute URL with the base, return as-is (idempotent)
  if (baseUrl && path.startsWith(baseUrl)) {
    return path;
  }
  
  // If it's a full URL (starts with http), return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

const getWorkspaceIdFromHeaders = (headers?: HeadersInit): string | null => {
  if (!headers) return null;

  if (headers instanceof Headers) {
    return headers.get('X-Workspace-Id');
  }

  if (Array.isArray(headers)) {
    const match = headers.find(([key]) => key.toLowerCase() === 'x-workspace-id');
    return match?.[1] ?? null;
  }

  const recordHeaders = headers as Record<string, string>;
  const matchingKey = Object.keys(recordHeaders).find((key) => key.toLowerCase() === 'x-workspace-id');
  return matchingKey ? recordHeaders[matchingKey] : null;
};

/**
 * Get authorization headers for API requests
 * Returns headers object with Bearer token if available
 */
export const getAuthHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  // Add workspace ID if available
  const workspaceId = localStorage.getItem('workspaceId');
  if (workspaceId) {
    headers['X-Workspace-Id'] = workspaceId;
  }
  
  return headers;
};

/**
 * Authenticated fetch wrapper that automatically adds Authorization header
 * For FormData uploads, don't set Content-Type - let the browser set it
 * 
 * On native platforms: if a request returns 401, attempts one token refresh
 * and retries the request with the new token before propagating the failure.
 * On web: if an authenticated request returns 401, retries once with a freshly
 * requested token so focus/visibility refreshes can recover without a full reload.
 */
export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const requestedWorkspaceId = getWorkspaceIdFromHeaders(options.headers) ?? localStorage.getItem('workspaceId');

  const doFetch = async (requestUrlOverride?: string): Promise<Response> => {
    const isFormData = options.body instanceof FormData;

    // Get auth headers but exclude Content-Type for FormData
    const authHeaders: Record<string, string> = {};
    let token: string | null = null;
    if (accessTokenProvider) {
      try {
        token = await withTimeout(
          accessTokenProvider(),
          ACCESS_TOKEN_PROVIDER_TIMEOUT_MS,
          '[authFetch] Access token provider timed out'
        );
      } catch {
        // Fail-open so requests are still sent and can recover via 401-refresh path.
        token = null;
      }
    }
    if (token) authHeaders['Authorization'] = `Bearer ${token}`;

    if (requestedWorkspaceId) {
      authHeaders['X-Workspace-Id'] = requestedWorkspaceId;
    }

    // Only add Content-Type for non-FormData requests
    if (!isFormData) {
      authHeaders['Content-Type'] = 'application/json';
    }

    const headers = {
      ...authHeaders,
      ...options.headers,
    };

    // Build the full URL (adds base URL for native mobile apps)
    const fullUrl = requestUrlOverride ?? buildApiUrl(url);
    const isNative = isNativeRuntime();
    if (!isNative || isFormData) {
      return fetch(fullUrl, {
        ...options,
        headers,
      });
    }

    const nativeMethod = (options.method ?? 'GET').toUpperCase();
    const nativeRequestPromise = CapacitorHttp.request({
      url: fullUrl,
      method: nativeMethod,
      headers,
      data: options.body as any,
      connectTimeout: NATIVE_HTTP_CONNECT_TIMEOUT_MS,
      readTimeout: NATIVE_HTTP_READ_TIMEOUT_MS,
    }) as Promise<NativeHttpResult>;

    const nativeResult = await withTimeout(
      nativeRequestPromise,
      NATIVE_FETCH_HARD_TIMEOUT_MS,
      '[authFetch] Native HTTP request timed out'
    );

    return toResponseFromNative(nativeResult);
  };

  const method = (options.method ?? 'GET').toUpperCase();

  let response = await doFetch();

  if (!options.signal?.aborted) {
    for (let attempt = 0; attempt < TRANSIENT_RETRY_DELAYS_MS.length; attempt += 1) {
      if (!shouldRetryTransient(method, response.status)) {
        break;
      }

      await wait(TRANSIENT_RETRY_DELAYS_MS[attempt]);
      response = await doFetch();
    }
  }

  // Railway/frontend proxy safety net: if relative /api calls return 503 on web,
  // retry once directly against the API domain to bypass transient proxy routing issues.
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  const isRelativeApiPath = normalizedPath.startsWith('/api/');
  if (!isNativeRuntime() && response.status === 503 && isRelativeApiPath) {
    const directUrl = `${WEB_API_FALLBACK_BASE}${normalizedPath}`;
    response = await doFetch(directUrl);
  }

  // On native platforms, retry once on 401 after refreshing the token.
  // This catches edge cases where the proactive refresh in getAccessToken() didn't fire
  // (e.g., token expired between getAccessToken and the actual fetch).
  if (response.status === 401 && isNativeRuntime()) {
    try {
      // Dynamic import to avoid circular dependency (useNativeAuth imports from api)
      const { refreshNativeTokens } = await import('@/hooks/useNativeAuth');
      console.debug('[authFetch] Got 401, attempting token refresh for retry...');
      const refreshed = await refreshNativeTokens();
      if (refreshed) {
        console.debug('[authFetch] Token refreshed, retrying request');
        // Retry the request with the new token
        return doFetch();
      } else {
        console.debug('[authFetch] Token refresh failed, returning 401');
      }
    } catch {
      // If refresh import or call fails, return the original 401
    }
  }

  if (response.status === 401 && !isNativeRuntime() && accessTokenProvider && !options.signal?.aborted) {
    response = await doFetch();
  }

  // Intercept 402 Payment Required for trial gating
  if (response.status === 402) {
    try {
      const body = await response.clone().json();
      const code = body?.code;
      if (code === 'trial_not_started' || code === 'trial_expired') {
        const { useBillingStore } = await import('@/stores/useBillingStore');
        useBillingStore.getState().setGateReason(code, requestedWorkspaceId);
      }
    } catch {
      // If parsing fails, still return the response so callers can handle it
    }
  }

  return response;
};
